import { ChevronRight } from "@mui/icons-material";
import {
  Box,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { ElementType, KeyboardEvent, MouseEventHandler } from "react";

interface UserMenuItemRowProps {
  icon: ElementType;
  label: string;
  onClick: MouseEventHandler<HTMLDivElement>;
  tone?: "default" | "danger";
  showChevron?: boolean;
}

export function UserMenuItemRow({
  icon: Icon,
  label,
  onClick,
  tone = "default",
  showChevron = true,
}: UserMenuItemRowProps) {
  const theme = useTheme();
  const isDanger = tone === "danger";

  const iconBg = isDanger
    ? alpha(theme.palette.error.main, 0.1)
    : alpha(theme.palette.primary.main, 0.1);
  const iconColor = isDanger ? "error.main" : "primary.main";

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(event as unknown as Parameters<MouseEventHandler<HTMLDivElement>>[0]);
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.25,
        py: 1.1,
        borderRadius: 2.5,
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": {
          bgcolor: isDanger
            ? alpha(theme.palette.error.main, 0.06)
            : "action.hover",
        },
        "&:active": {
          bgcolor: isDanger
            ? alpha(theme.palette.error.main, 0.1)
            : "action.selected",
        },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          bgcolor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 20, color: iconColor }} />
      </Box>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          flex: 1,
          minWidth: 0,
          color: isDanger ? "error.main" : "text.primary",
        }}
      >
        {label}
      </Typography>

      {showChevron ? (
        <ChevronRight
          sx={{
            fontSize: 20,
            color: "text.disabled",
            flexShrink: 0,
          }}
        />
      ) : null}
    </Box>
  );
}
